import Endpoints from 'constants/Endpoints';
import {clearResource, setResource, setSaveStatusClosed, setSaveStatusError, setSaveStatusLoading} from 'redux/actions'
import reduxStore from 'redux/store'
import {RestRequestBLOB, RestRequestDELETE, RestRequestGET, RestRequestPOST, RestRequestPUT} from 'libs/RestRequest';
import isResponseValid from "../libs/isResponseValid";
import Tr from "../translations/Translations";
import applySearchParams from "../libs/applySearchParams";
import applySortParams from "../libs/applySortParams";
import downloadFileWithPreloadUrl from "../libs/downloadFileWithPreloadUrl";
import PurchasingGroupSectionService from "./PurchasingGroupSectionService";
import {PurchasingGroupStageSections} from "../pages/purchaser/PurchasingGroup/stages/constants/PurchasingGroupStageSections";

const PURCHASING_GROUP_HEADER_RESOURCE_ID = "purchasingGroupHeader";
const PURCHASING_GROUP_MEMBERS_RESOURCE_ID = "purchasingGroupMembers";
const PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID = "purchasingGroupMembersDocuments";
const PURCHASING_GROUP_OPINIONS_RESOURCE_ID = "purchasingGroupOpinions";
const PURCHASING_GROUP_MEMBER_RESOURCE_ID = (memberId) => `purchasingGroupMember_${memberId}`;
const PURCHASING_GROUP_OPINION_MESSAGES_RESOURCE_ID = (opinionId) => `purchasingGroupOpinion_${opinionId}`;

const __get = async (url, resourcesName) => {
    try {
        let res = await RestRequestGET(url)
        if (resourcesName) {
            reduxStore.dispatch(setResource(resourcesName, res))
        }
        return res;
    } catch (error) {
    }
}

const __save = async (url, values) => {
    reduxStore.dispatch(setSaveStatusLoading());
    try {
        const resp = await RestRequestPOST(url, values)
        isResponseValid(resp, (e) => {
            throw new Error(e);
        })
        reduxStore.dispatch(setSaveStatusClosed());
        return resp;
    } catch (e) {
        console.error(e)
        reduxStore.dispatch(setSaveStatusError(Tr('internal_server_error')))
    }
}

const __put = async (url, values) => {
    reduxStore.dispatch(setSaveStatusLoading());
    try {
        const resp = await RestRequestPUT(url, values)
        isResponseValid(resp, (e) => {
            throw new Error(e);
        })
        reduxStore.dispatch(setSaveStatusClosed());
        return resp;
    } catch (e) {
        console.error(e)
        reduxStore.dispatch(setSaveStatusError(Tr('internal_server_error')))
    }
}


const getHeader = async (groupId) => {
    await __get(Endpoints.PURCHASING_GROUP_HEADER(!groupId ? -1 : groupId), PURCHASING_GROUP_HEADER_RESOURCE_ID)
}
const getRelatedObjects = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_RELATED_OBJECTS(groupId))
}

const getPurchasingGroupList = async (searchData) => {
    const {pagination, search, sort} = searchData;

    let params = {
        ...pagination
    }
    params = applySearchParams(search, params);
    params = applySortParams(sort, params);
    return await RestRequestGET(Endpoints.PURCHASING_GROUP_LIST(), {params});
};

const getPurchasingGroupListActions = () => {
    return __get(Endpoints.PURCHASING_GROUP_LIST_ACTIONS())
}

const getPurchasingGroupMemberList = async (searchData) => {
    const {pagination, search, sort} = searchData;

    let params = {
        ...pagination
    }
    params = applySearchParams(search, params);
    params = applySortParams(sort, params);
    return await RestRequestGET(Endpoints.PURCHASING_GROUP_MEMBER_LIST(), {params});
};

const getArchiveList = async (groupId, archiveType) => {
    return await __get(Endpoints.PURCHASING_GROUP_ARCHIVE_LIST(groupId, archiveType))
}
const getArchiveElement = async (groupId, archiveType, archiveId) => {
    return await __get(Endpoints.PURCHASING_GROUP_ARCHIVE_ID(groupId, archiveType, archiveId))
}


const getBasicData = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_BASIC_DATA(!groupId ? -1 : groupId))
}
const saveBasicData = async (groupId, values) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_BASIC_DATA(!groupId ? -1 : groupId), values)
    if (isResponseValid(resp, (e) => {
        reduxStore.dispatch(setSaveStatusError(e));
    })) {
        reduxStore.dispatch(setSaveStatusClosed());
        return resp.result;
    }
}


const getCommission = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_COMMISSION(groupId))
}
const saveCommission = async (groupId, values) => {
    return await __save(Endpoints.PURCHASING_GROUP_COMMISSION(groupId), values)
}
const getCommissionHistory = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_COMMISSION_HISTORY(groupId))
}
const markHistorical = async (groupId, id) => {
    return await __put(Endpoints.PURCHASING_GROUP_COMMISSION_MARK_HISTORICAL(groupId, id));
}


const getSchedule = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_SCHEDULE(groupId))
}
const saveSchedule = async (groupId, values) => {
    try {
        reduxStore.dispatch(setSaveStatusLoading());
        const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_SCHEDULE(groupId), values);
        reduxStore.dispatch(setSaveStatusClosed());
        return resp;
    } catch (e) {
    }
}


const getMembers = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS(groupId), PURCHASING_GROUP_MEMBERS_RESOURCE_ID)
}
const getMembersRow = async (groupId, memberId) => {
    const members = await __get(Endpoints.PURCHASING_GROUP_MEMBERS(groupId), PURCHASING_GROUP_MEMBERS_RESOURCE_ID);
    if (members.length === 1) {
        const member = members[0];
        const {resources} = reduxStore.getState();
        const res = resources[PURCHASING_GROUP_MEMBERS_RESOURCE_ID].map(x => x.identity === member.identity ? member : x)
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_RESOURCE_ID, res))
    }
}

const updateMemberName = async (groupId, memberId, values) => {
    const resp = await RestRequestPUT(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID(groupId, memberId), values)
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = resources[PURCHASING_GROUP_MEMBERS_RESOURCE_ID].map(x => x.identity === resp.identity ? resp : x)
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_RESOURCE_ID, res))
    }
}

const rejectMember = async (groupId, memberId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_REJECT(groupId, memberId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = resources[PURCHASING_GROUP_MEMBERS_RESOURCE_ID].map(x => x.identity === resp.identity ? resp : x)
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_RESOURCE_ID, res))
        PurchasingGroupSectionService.getSections(groupId, PurchasingGroupStageSections.MEMBERS)
    }
}

const restoreMember = async (groupId, memberId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_RESTORE(groupId, memberId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = resources[PURCHASING_GROUP_MEMBERS_RESOURCE_ID].map(x => x.identity === resp.identity ? resp : x)
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_RESOURCE_ID, res))
        PurchasingGroupSectionService.getSections(groupId, PurchasingGroupStageSections.MEMBERS)
    }
}

const withdrawParticipationMember = async (groupId, memberId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_WITHDRAW_PARTICIPATION(groupId, memberId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {canConfirm, confirmationDate} = resp;
        const {resources} = reduxStore.getState();
        const member = resources[PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId)];
        if (member) {
            const res = {...member, canConfirm, confirmationDate}
            reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId), res))
        }
    }
}

const confirmParticipationMember = async (groupId, memberId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_CONFIRM_PARTICIPATION(groupId, memberId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {canConfirm, confirmationDate} = resp;
        const {resources} = reduxStore.getState();
        const member = resources[PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId)];
        if (member) {
            const res = {...member, canConfirm, confirmationDate}
            reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId), res))
        }
    }
}


const resendInviteNotification = async (groupId, memberId) => {
    await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_RESEND_INVITE_NOTIFICATION(groupId, memberId))
}

const deleteMember = async (groupId, memberId) => {
    const resp = await RestRequestDELETE(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID(groupId, memberId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = resources[PURCHASING_GROUP_MEMBERS_RESOURCE_ID].filter(x => x.identity !== memberId)
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_RESOURCE_ID, res))
    }
}

const getMembersAvailableConcerns = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_AVAILABLE_CONCERNS(groupId));
}

const addMembers = async (groupId, values) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER(groupId), values)
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = [...resources[PURCHASING_GROUP_MEMBERS_RESOURCE_ID], ...resp.result]
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_RESOURCE_ID, res))
        return resp.result;
    }

}

const getAllMembersDocuments = async (groupId, documentsApiPathType) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS(groupId, documentsApiPathType), PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID)
}
const addAllMembersDocuments = async (groupId, documentsApiPathType, values) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS(groupId, documentsApiPathType), {documents: values})
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const noErrorsDocs = resp.filter(d => d.error === null);

        if (noErrorsDocs.length > 0) {
            const {resources} = reduxStore.getState();
            const res = {
                ...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID],
                documents: [...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID].documents, ...noErrorsDocs]
            }
            reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID, res))
        }
        return resp;
    }
}
const generateGroupAgreementDocument = async (groupId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS_GENERATE_GROUP_AGREEMENT(groupId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = {
            ...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID],
            documents: [...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID].documents, ...resp.result]
        }
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID, res))
        return resp.result;
    }
}

const updateAllMembersDocument = async (groupId, {documentsApiPathType, documentId}, description) => {
    const resp = await RestRequestPUT(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS_ID(groupId, documentsApiPathType, documentId), {description})
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = {
            ...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID],
            documents: [...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID].documents.map(x => x.identity === resp.identity ? resp : x)]
        }
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID, res))
    }
}
const publishAllMembersDocument = async (groupId, documentsApiPathType, documentId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS_ID_PUBLISH(groupId, documentsApiPathType, documentId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = {
            ...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID],
            documents: [...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID].documents.map(x => x.identity === resp.result.identity ? resp.result : x)]
        }
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID, res))
    }
}
const unpublishAllMembersDocument = async (groupId, documentsApiPathType, documentId) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS_ID_UNPUBLISH(groupId, documentsApiPathType, documentId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const res = {
            ...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID],
            documents: [...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID].documents.map(x => x.identity === resp.result.identity ? resp.result : x)]
        }
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID, res))
    }
}


const deleteAllMembersDocument = async (groupId, documentsApiPathType, documentId) => {
    const resp = await RestRequestDELETE(Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS_ID(groupId, documentsApiPathType, documentId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();

        const res = {
            ...resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID],
            documents: resources[PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID].documents.filter(x => x.identity !== documentId)
        }
        reduxStore.dispatch(setResource(PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID, res))
    }
}

const downloadAllMembersDocument = async (demandId, documentsApiPathType, identity, openInBrowser) => {
    await downloadFileWithPreloadUrl(
        Endpoints.PURCHASING_GROUP_MEMBERS_DOCUMENTS_ID_URL(demandId, documentsApiPathType, identity, openInBrowser),
        openInBrowser);
}

const getMember = async (groupId, memberId) => {
    await __get(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID(groupId, memberId), PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId))
}
const addMemberDocuments = async (groupId, memberId, values) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_DOCUMENT(groupId, memberId), {documents: values})
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const noErrorsDocs = resp.filter(d => d.error === null);

        if (noErrorsDocs.length > 0) {
            const {resources} = reduxStore.getState();
            const resourceId = PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId);
            const cur = resources[resourceId];
            const res = {
                ...cur,
                memberDocuments: {
                    ...cur.memberDocuments,
                    documents: [...cur.memberDocuments.documents, ...noErrorsDocs]
                }
            }
            reduxStore.dispatch(setResource(resourceId, res))
        }
        return resp;
    }
}

const updateMemberDocument = async (groupId, {memberId, documentId}, description) => {
    const resp = await RestRequestPUT(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_DOCUMENT_ID(groupId, memberId, documentId), {description})
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const resourceId = PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId);
        const cur = resources[resourceId];

        const res = {
            ...cur,
            memberDocuments: {
                ...cur.memberDocuments,
                documents: cur.memberDocuments.documents.map(x => x.identity === documentId ? resp : x)
            }
        }
        reduxStore.dispatch(setResource(resourceId, res))
    }
}
const deleteMemberDocument = async (groupId, memberId, documentId) => {
    const resp = await RestRequestDELETE(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_DOCUMENT_ID(groupId, memberId, documentId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const resourceId = PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId);
        const cur = resources[resourceId];

        const res = {
            ...cur,
            memberDocuments: {
                ...cur.memberDocuments,
                documents: cur.memberDocuments.documents.filter(x => x.identity !== documentId)
            }
        }
        reduxStore.dispatch(setResource(resourceId, res))

    }
}
const downloadMemberDocument = async (demandId, memberId, documentId, openInBrowser) => {
    await downloadFileWithPreloadUrl(
        Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_DOCUMENT_ID_URL(demandId, memberId, documentId, openInBrowser),
        openInBrowser);
}


const getAvailableMemberUsers = async (groupId, memberId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_USER(groupId, memberId));
}
const addMemberUser = async (groupId, memberId, userIds) => {
    const resp = await RestRequestPOST(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_USER(groupId, memberId), {list: userIds})
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const resourceId = PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId);
        const cur = resources[resourceId];
        const res = {
            ...cur,
            users: [...cur.users, ...resp]
        }
        reduxStore.dispatch(setResource(resourceId, res));
        await getMembersRow(groupId, memberId)
    }
}
const deleteMemberUser = async (groupId, memberId, userId) => {
    const resp = await RestRequestDELETE(Endpoints.PURCHASING_GROUP_MEMBERS_MEMBER_ID_USER_ID(groupId, memberId, userId))
    if (isResponseValid(resp, (e) => {
        throw new Error(e)
    })) {
        const {resources} = reduxStore.getState();
        const resourceId = PURCHASING_GROUP_MEMBER_RESOURCE_ID(memberId);
        const cur = resources[resourceId];

        const res = {
            ...cur,
            users: cur.users.filter(x => x.identity !== userId)
        }
        reduxStore.dispatch(setResource(resourceId, res))
        await getMembersRow(groupId, memberId)
    }
}


//opinion

const getOpinions = async (groupId, type) => {
    return __get(Endpoints.PURCHASING_GROUP_OPINION(groupId, type), PURCHASING_GROUP_OPINIONS_RESOURCE_ID)
}

const getOpinionAvailableMembers = async (groupId, type) => {
    return __get(Endpoints.PURCHASING_GROUP_OPINION_AVAILABLE_MEMBERS(groupId, type))
}

const addOpinionForMember = async (groupId, type, memberId) => {
    const newOpinion = await __save(Endpoints.PURCHASING_GROUP_OPINION_MEMBER_ID(groupId, type, memberId));
    if (newOpinion) {
        const {resources} = reduxStore.getState();
        const cur = resources[PURCHASING_GROUP_OPINIONS_RESOURCE_ID];

        const newRes = [
            ...cur,
            newOpinion
        ]
        reduxStore.dispatch(setResource(PURCHASING_GROUP_OPINIONS_RESOURCE_ID, newRes))
        return newOpinion;
    }
}

const getOpinionMessages = async (groupId, type, opinionId, editable) => {
    if (opinionId > 0) {
        const messages = await __get(Endpoints.PURCHASING_GROUP_OPINION_ID(groupId, type, opinionId), PURCHASING_GROUP_OPINION_MESSAGES_RESOURCE_ID(opinionId));
        if (messages) {
            const ids = messages.filter(x => x.read === false).map(x => x.identity);
            if (ids.length > 0) {
                RestRequestPOST(Endpoints.PURCHASING_GROUP_OPINION_ID_MARK_AS_READ(groupId, type, opinionId), {list: ids}).then(() => {
                    PurchasingGroupSectionService.getSections(groupId, type === "AGREEMENT" ? PurchasingGroupStageSections.OPINION : PurchasingGroupStageSections.OPINION_SWZ)
                })
                const {resources} = reduxStore.getState();
                const cur = resources[PURCHASING_GROUP_OPINIONS_RESOURCE_ID];
                const newRes = cur.map(x => x.identity === opinionId ? {...x, unreadCount: 0} : x)
                reduxStore.dispatch(setResource(PURCHASING_GROUP_OPINIONS_RESOURCE_ID, newRes))
            }
        }
    } else {
        await addEmptyOpinionMessage(groupId, type, opinionId, editable);
    }
}

const saveOpinionMessage = async (groupId, type, opinionId, message) => {
    const newMessage = await RestRequestPOST(Endpoints.PURCHASING_GROUP_OPINION_ID(groupId, type, opinionId), message);
    if (newMessage) {

        const {resources} = reduxStore.getState();
        const resourceId = PURCHASING_GROUP_OPINION_MESSAGES_RESOURCE_ID(opinionId);
        const cur = opinionId > 0 ? resources[resourceId] || [] : [];

        const res = message.identity === 0 ? [
            ...cur,
            newMessage
        ] : cur.map(x => x.identity === message.identity ? newMessage : x)
        reduxStore.dispatch(setResource(resourceId, res))

        return newMessage;
    }
}

const addEmptyOpinionMessage = async (groupId, type, opinionId, editable) => {
    let newMessage;
    if (editable) {
        if (opinionId > 0) {
            newMessage = await RestRequestPOST(Endpoints.PURCHASING_GROUP_OPINION_ID_MESSAGE_NEW(groupId, type, opinionId));
        } else {
            newMessage = {
                identity: 0,
                memberMessage: true,
                author: null,
                contents: "",
                status: "SAVED",
                createDate: null,
                modifiedDate: null,
                sendDate: null,
                read: true
            }
        }
    }

    const {resources} = reduxStore.getState();
    const resourceId = PURCHASING_GROUP_OPINION_MESSAGES_RESOURCE_ID(opinionId);
    const cur = resources[resourceId] || [];
    const res = [
        ...cur,
    ]
    if (newMessage) {
        res.push(newMessage)
    }

    reduxStore.dispatch(setResource(resourceId, res))
}

const deleteOpinionMessage = async (groupId, type, opinionId, messageId) => {

    const resp = await RestRequestDELETE(Endpoints.PURCHASING_GROUP_OPINION_ID_MESSAGE_ID(groupId, type, opinionId, messageId));
    if (resp.success) {
        const {resources} = reduxStore.getState();
        const resourceId = PURCHASING_GROUP_OPINION_MESSAGES_RESOURCE_ID(opinionId);
        const cur = resources[resourceId] || [];
        const res = cur.filter(x => x.identity !== messageId);
        reduxStore.dispatch(setResource(resourceId, res))
    }

}

const canFinishPreparing = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_FINISH_PREPARING(groupId));
}

const finishPreparingStage = async (groupId) => {
    return await __save(Endpoints.PURCHASING_GROUP_FINISH_PREPARING(groupId), null);
}

//papersGenerator
const getGzmManagementResolution = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_MANAGEMENT_RESOLUTION(groupId))
}
const printGzmManagementResolution = async (groupId, data) => {
    return __print(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_MANAGEMENT_RESOLUTION(groupId), data);
}

const getGzmLeadingDocument = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_LEADING_DOCUMENT(groupId))
}
const printGzmLeadingDocument = async (groupId, data) => {
    return __print(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_LEADING_DOCUMENT(groupId), data)
}

const getGzmDocumentToSign = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_DOCUMENT_TO_SIGN(groupId))
}

const printGzmDocumentToSign = async (groupId, data) => {
    return __print(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_DOCUMENT_TO_SIGN(groupId), data)
}

const getGzmDraftGroupAgreement = async (groupId) => {
    return await __get(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_DRAFT_GROUP_AGREEMENT(groupId))
}

const printGzmDraftGroupAgreement = async (groupId, data) => {
    return __print(Endpoints.PURCHASING_GROUP_MEMBERS_PAPERS_GENERATOR_GZM_DRAFT_GROUP_AGREEMENT(groupId), data)
}

const __print = (url, data) => {
    return RestRequestBLOB(url, {data, method: 'post'});
}

const clearResources = (resourceId) => reduxStore.dispatch(clearResource(resourceId))


export default {
    getHeader,
    getRelatedObjects,
    getPurchasingGroupList,
    getPurchasingGroupListActions,
    getPurchasingGroupMemberList,
    getArchiveList,
    getArchiveElement,

    //basic-data
    getBasicData,
    saveBasicData,

    //commission
    getCommission,
    saveCommission,
    getCommissionHistory,
    markHistorical,

    //schedule
    getSchedule,
    saveSchedule,

    //members
    getMembers,
    updateMemberName,
    rejectMember,
    restoreMember,
    confirmParticipationMember,
    withdrawParticipationMember,
    resendInviteNotification,
    deleteMember,
    getMembersAvailableConcerns,
    addMembers,
    //member dokumenty główne
    getAllMembersDocuments,
    addAllMembersDocuments,
    generateGroupAgreementDocument,
    updateAllMembersDocument,
    publishAllMembersDocument,
    unpublishAllMembersDocument,
    deleteAllMembersDocument,
    downloadAllMembersDocument,
    //member konkretny member
    getMember,
    addMemberDocuments,
    updateMemberDocument,
    deleteMemberDocument,
    downloadMemberDocument,
    getAvailableMemberUsers,
    addMemberUser,
    deleteMemberUser,

    //opinion
    getOpinions,
    getOpinionAvailableMembers,
    addOpinionForMember,
    getOpinionMessages,
    saveOpinionMessage,
    addEmptyOpinionMessage,
    deleteOpinionMessage,

    //finishPreparing
    canFinishPreparing,
    finishPreparingStage,

    //papersGenerator
    getGzmManagementResolution,
    printGzmManagementResolution,
    getGzmLeadingDocument,
    printGzmLeadingDocument,
    getGzmDocumentToSign,
    printGzmDocumentToSign,
    getGzmDraftGroupAgreement,
    printGzmDraftGroupAgreement,

    clearResources,
    PURCHASING_GROUP_HEADER_RESOURCE_ID,
    PURCHASING_GROUP_MEMBERS_RESOURCE_ID,
    PURCHASING_GROUP_MEMBERS_DOCUMENTS_RESOURCE_ID,
    PURCHASING_GROUP_MEMBER_RESOURCE_ID,
    PURCHASING_GROUP_OPINIONS_RESOURCE_ID,
    PURCHASING_GROUP_OPINION_MESSAGES_RESOURCE_ID,
}